import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { ExternalLink, MoreVertical, Plus } from "lucide-react";
import {
    Avatar,
    Input,
    Menu,
    MenuItem,
    Pill,
    Table,
    TableHead,
    TableRow,
    VbButton,
    VbEmpty,
} from "@/components/vb";
import ConfirmDialog from "@/components/settings/components/ConfirmDialog";
import DomainFavicon from "@/components/ui/DomainFavicon";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { useWorkspaceRole } from "@/hooks/useWorkspaceRole";
import { projectsInstance } from "@/services/ProjectsService";
import { deleteWorkspaceCompany, renameWorkspaceCompany } from "@/services/workspaceService";
import { WorkspaceCompany } from "@/types/workspace";

const COLUMNS = "36px minmax(0,2fr) minmax(0,1.5fr) minmax(0,1fr) minmax(0,1fr) 36px";

type CompanySummary = {
    lastReportDate: string | null;
    score: number | null;
};

const formatMonth = (isoDate: string): string | null => {
    const parsed = new Date(isoDate);
    return Number.isNaN(parsed.getTime())
        ? null
        : parsed.toLocaleDateString(undefined, { year: "numeric", month: "short" });
};

const formatWebsiteHost = (url?: string | null): string | null => {
    if (!url) return null;
    try {
        const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        return new URL(normalized).host || url;
    } catch {
        return url;
    }
};

const parseScore = (overallScore: unknown): number | null => {
    if (overallScore === null || overallScore === undefined) return null;
    const parsed =
        typeof overallScore === "number" ? overallScore : Number.parseFloat(String(overallScore));
    if (!Number.isFinite(parsed)) return null;
    return parsed <= 1 ? Math.round(parsed * 100) : Math.round(parsed);
};

/** The report dashboard's tier bands (AITS-491), reused so a company's score
 *  reads the same here as it does on its own report. */
export const scoreTier = (score: number): { label: string; tone: "critical" | "issue" | "healthy" } => {
    // Bands mirror ScoreBar (AITS-491) exactly. The tier ramp is 4-wide and the
    // pill vocabulary is 3-wide, so Unverified and Trusted share the warn tone —
    // what matters is that only Authority reads green, as it does on the report.
    if (score < 70) return { label: "Invisible", tone: "critical" };
    if (score < 80) return { label: "Unverified", tone: "issue" };
    if (score < 93) return { label: "Trusted", tone: "issue" };
    return { label: "Authority", tone: "healthy" };
};

const SettingsCompanies: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useAuth();
    const { activeWorkspace, refreshWorkspaces } = useWorkspace();
    const { isWorkspaceOwner } = useWorkspaceRole();

    const [companySummaryById, setCompanySummaryById] = useState<Record<number, CompanySummary>>({});
    const [companyToRename, setCompanyToRename] = useState<WorkspaceCompany | null>(null);
    const [renamedCompanyName, setRenamedCompanyName] = useState("");
    const [renamingCompany, setRenamingCompany] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState<WorkspaceCompany | null>(null);
    const [deletingCompany, setDeletingCompany] = useState(false);

    const workspaceCompanies = activeWorkspace?.companies ?? [];

    useEffect(() => {
        const companies = activeWorkspace?.companies ?? [];
        if (!companies.length || !token) {
            setCompanySummaryById({});
            return;
        }

        let cancelled = false;

        const loadCompanySummaries = async () => {
            const entries = await Promise.all(
                companies.map(async (company) => {
                    const empty = {
                        lastReportDate: null,
                        score: null,
                    } satisfies CompanySummary;

                    if (!company.lastReportId) {
                        return [company.company_id, empty] as const;
                    }

                    try {
                        const report = await projectsInstance.getReportById(
                            company.lastReportId,
                            token,
                        );
                        return [
                            company.company_id,
                            {
                                lastReportDate: report?.created_ts
                                    ? formatMonth(report.created_ts)
                                    : null,
                                score: parseScore(report?.overall_score),
                            },
                        ] as const;
                    } catch {
                        return [company.company_id, empty] as const;
                    }
                }),
            );

            if (cancelled) return;

            setCompanySummaryById(
                entries.reduce<Record<number, CompanySummary>>((acc, [companyId, summary]) => {
                    acc[companyId] = summary;
                    return acc;
                }, {}),
            );
        };

        void loadCompanySummaries();

        return () => {
            cancelled = true;
        };
    }, [activeWorkspace?.companies, token]);

    const handleAddCompany = (): void => {
        // The shell owns the dialog and reads this off nav state, so open it in
        // place. This used to route to Home first (AITS-540 removed Home); with
        // `/` now resolving into a company view, keeping the target meant being
        // thrown out of Settings just to add a company.
        navigate(location.pathname + location.search, {
            replace: true,
            state: { openReportDialog: true },
        });
    };

    const handleOpenLatestReport = (company: WorkspaceCompany): void => {
        if (!company.lastReportId) return;
        navigate(`/reports/${company.company_id}/${company.lastReportId}`);
    };

    const handleRenameCompany = async (): Promise<void> => {
        if (!activeWorkspace || !companyToRename || !renamedCompanyName.trim()) {
            return;
        }
        try {
            setRenamingCompany(true);
            await renameWorkspaceCompany(
                activeWorkspace.id,
                companyToRename.company_id,
                renamedCompanyName.trim(),
            );
            await refreshWorkspaces();
            toast.success("Company renamed");
            setCompanyToRename(null);
            setRenamedCompanyName("");
        } catch {
            toast.error("Failed to rename company");
        } finally {
            setRenamingCompany(false);
        }
    };

    const handleDeleteCompany = async (): Promise<void> => {
        if (!activeWorkspace || !companyToDelete) {
            return;
        }
        try {
            setDeletingCompany(true);
            await deleteWorkspaceCompany(activeWorkspace.id, companyToDelete.company_id);
            await refreshWorkspaces();
            toast.success("Company deleted from workspace");
            setCompanyToDelete(null);
        } catch {
            toast.error("Failed to delete company");
        } finally {
            setDeletingCompany(false);
        }
    };

    const companyCount = workspaceCompanies.length;

    return (
        <>
            <section className="vb-card">
                <div className="vb-ws-summary">
                    <Avatar
                        size="xl"
                        kind="workspace"
                        name={activeWorkspace?.name ?? "?"}
                        decorative
                    />
                    <div>
                        <div className="vb-ws-summary__name">{activeWorkspace?.name ?? "—"}</div>
                        <div className="vb-ws-summary__sub">
                            Workspace · {companyCount} {companyCount === 1 ? "company" : "companies"}
                        </div>
                    </div>
                    <div className="vb-stat">
                        <span className="vb-stat__v">{companyCount}</span>
                        <span className="vb-stat__l">Companies</span>
                    </div>
                    <div>
                        <VbButton type="button" onClick={handleAddCompany}>
                            <Plus className="h-[13px] w-[13px]" aria-hidden="true" />
                            Add company
                        </VbButton>
                    </div>
                </div>
            </section>

            <section className="vb-card">
                <div className="vb-card-hd">
                    <div className="vb-card-hd__left">
                        <h2>Companies in this workspace</h2>
                        <p className="vb-card-hd__sub">
                            Each company gets its own AI Trust Signal report and Virtual Buyer.
                        </p>
                    </div>
                </div>

                {companyCount === 0 ? (
                    <VbEmpty
                        title="No companies yet"
                        description="Add a company to start tracking how AI describes it."
                        action={
                            <VbButton type="button" onClick={handleAddCompany}>
                                Add company
                            </VbButton>
                        }
                    />
                ) : (
                    <Table columns={COLUMNS} minWidth={560} tight framed ariaLabel="Companies">
                        <TableHead>
                            <span />
                            <span>Company</span>
                            <span>Website</span>
                            <span>Last report</span>
                            <span>Score</span>
                            <span />
                        </TableHead>

                        {workspaceCompanies.map((company) => {
                            const summary = companySummaryById[company.company_id];
                            const host = formatWebsiteHost(company.company_url);
                            const tier = summary?.score != null ? scoreTier(summary.score) : null;
                            return (
                                <TableRow key={company.company_id}>
                                    <DomainFavicon
                                        name={company.company_name}
                                        url={company.company_url}
                                        size={30}
                                    />
                                    <span>
                                        {company.lastReportId ? (
                                            <button
                                                type="button"
                                                className="vb-dt-name text-left"
                                                onClick={() => handleOpenLatestReport(company)}
                                            >
                                                {company.company_name}
                                            </button>
                                        ) : (
                                            <span className="vb-dt-name">
                                                {company.company_name}
                                            </span>
                                        )}
                                    </span>
                                    <span data-label="Website">
                                        {host ? (
                                            <a
                                                href={
                                                    /^https?:\/\//i.test(company.company_url ?? "")
                                                        ? (company.company_url as string)
                                                        : `https://${company.company_url}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="vb-dt-link"
                                            >
                                                <span>{host}</span>
                                                <ExternalLink
                                                    className="h-3 w-3 shrink-0"
                                                    aria-hidden="true"
                                                />
                                            </a>
                                        ) : (
                                            <span className="vb-dt-last">—</span>
                                        )}
                                    </span>
                                    <span className="vb-dt-last" data-label="Last report">
                                        {summary?.lastReportDate ?? "—"}
                                    </span>
                                    <span data-label="Score">
                                        {tier ? (
                                            <Pill tone={tier.tone} quiet>
                                                {summary?.score} · {tier.label}
                                            </Pill>
                                        ) : (
                                            <span className="vb-dt-last">—</span>
                                        )}
                                    </span>
                                    <span>
                                        {isWorkspaceOwner && (
                                            <Menu
                                                align="end"
                                                trigger={
                                                    <button
                                                        type="button"
                                                        className="vb-dt-more"
                                                        aria-label={`Row actions for ${company.company_name}`}
                                                    >
                                                        <MoreVertical
                                                            className="h-[14px] w-[14px]"
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                }
                                            >
                                                <MenuItem
                                                    onSelect={() => {
                                                        setCompanyToRename(company);
                                                        setRenamedCompanyName(company.company_name);
                                                    }}
                                                >
                                                    Rename
                                                </MenuItem>
                                                <MenuItem
                                                    danger
                                                    onSelect={() => setCompanyToDelete(company)}
                                                >
                                                    Delete
                                                </MenuItem>
                                            </Menu>
                                        )}
                                    </span>
                                </TableRow>
                            );
                        })}
                    </Table>
                )}

                <div className="vb-dashed vb-dashed--row">
                    <div className="vb-dashed__ic">
                        <Plus className="h-[18px] w-[18px]" aria-hidden="true" />
                    </div>
                    <div className="vb-dashed__body">
                        <div className="vb-dashed__t">Adding another company?</div>
                        <div className="vb-dashed__d">
                            Each company runs its own monthly report and uses its own credit.
                        </div>
                    </div>
                    <VbButton type="button" variant="secondary" onClick={handleAddCompany}>
                        Add company
                    </VbButton>
                </div>
            </section>

            <ConfirmDialog
                open={companyToRename !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setCompanyToRename(null);
                        setRenamedCompanyName("");
                    }
                }}
                title="Rename company"
                description="This only changes the display name in AITS — reports keep their history."
                confirmLabel="Save name"
                pendingLabel="Saving…"
                pending={renamingCompany}
                confirmDisabled={!renamedCompanyName.trim()}
                onConfirm={() => void handleRenameCompany()}
            >
                {/* Matches Company.name (max_length=255) — same reason as the
                    workspace-name box: past it the rename fails as a DRF field
                    error and surfaces only as a generic toast. */}
                <Input
                    value={renamedCompanyName}
                    onChange={(event) => setRenamedCompanyName(event.target.value)}
                    placeholder="Company name"
                    aria-label="Company name"
                    maxLength={255}
                    disabled={renamingCompany}
                />
            </ConfirmDialog>

            <ConfirmDialog
                open={companyToDelete !== null}
                onOpenChange={(open) => {
                    if (!open) setCompanyToDelete(null);
                }}
                title="Delete company"
                description={
                    <>
                        Remove <strong>{companyToDelete?.company_name}</strong> from{" "}
                        {activeWorkspace?.name}? Its reports go with it.
                    </>
                }
                confirmLabel="Delete company"
                pendingLabel="Deleting…"
                destructive
                pending={deletingCompany}
                onConfirm={() => void handleDeleteCompany()}
            />
        </>
    );
};

export default SettingsCompanies;
